import { useState, useRef, ElementType, ComponentType, useEffect } from "react";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@repo/ui/components/ui/popover";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "@repo/ui/components/ui/toggle-group";
import { IconButton } from "@repo/ui/components/ui/icon-button";
import Icon from "@repo/ui/components/ui/icon/icon"; // Adjust to your icon import
import { useTranslations } from "next-intl";
import { cn } from "@repo/ui/lib/utils"; // adjust if not next-intl
import { Category } from "@repo/ui/components/ui/header/index";
import {
  brandCategorySearchUrl,
  brandSearchUrl,
} from "@repo/ui/lib/algolia-url";

type SecondaryProps = {
  closeAction?: () => void;
  items: Array<Category>;
  LinkCmp: ComponentType | ElementType;
  brandsLabel?: string;
};

const CLOSE_DELAY = 250;
const OPEN_DELAY = 200;

const Secondary = ({
  items,
  LinkCmp,
  closeAction,
  brandsLabel,
}: SecondaryProps) => {
  const [sub, setSub] = useState<string>();
  const t = useTranslations();

  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [hovered, setHovered] = useState<number | null>(null);

  const closeTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const openTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handlePopoverOpen = (index: number) => {
    if (closeTimeout.current) {
      clearTimeout(closeTimeout.current);
      closeTimeout.current = null;
    }
    if (openTimeout.current) clearTimeout(openTimeout.current);
    openTimeout.current = setTimeout(() => {
      setOpenIndex(index);
    }, OPEN_DELAY);
  };

  const handlePopoverClose = () => {
    if (openTimeout.current) {
      clearTimeout(openTimeout.current);
    }
    if (closeTimeout.current) clearTimeout(closeTimeout.current);
    closeTimeout.current = setTimeout(() => {
      setOpenIndex(null);
    }, CLOSE_DELAY);
  };

  useEffect(() => {
    return () => {
      if (closeTimeout.current) clearTimeout(closeTimeout.current);
    };
  }, []);

  const handleToggle = (value: string) => setSub(value);

  return (
    <div>
      {/* Desktop */}
      <nav className="max-md:hidden h-[50px] flex justify-center items-center gap-[40px] bg-primary-black text-primary-white dark:bg-primary-white dark:text-primary-black">
        {items.map((item, index) => (
          <Popover
            key={index}
            open={openIndex === index}
            onOpenChange={(open) => setOpenIndex(open ? index : null)}
            modal={false}
          >
            <PopoverTrigger asChild>
              <LinkCmp
                href={
                  item.isBrand
                    ? "/brand"
                    : `/categories/${item.id}/${item.title?.replace(/[^a-zA-Z0-9]/g, "-")}`
                }
                data-text={item.title}
                className={cn(
                  "h-full hover:underline hover:font-semibold text-[18px] font-medium max-md:text-primary-black max-md:dark:text-primary-white bold-hover-size",
                  "focus:outline-none focus-visible:outline-none",
                  { "underline font-semibold": openIndex === index },
                )}
                onMouseEnter={() => handlePopoverOpen(index)}
                onMouseLeave={handlePopoverClose}
              >
                {item.title}
              </LinkCmp>
            </PopoverTrigger>
            <PopoverContent
              side="bottom"
              align="center"
              sideOffset={0}
              className="flex w-max p-10"
              onMouseEnter={() => handlePopoverOpen(index)}
              onMouseLeave={handlePopoverClose}
            >
              <div className="flex flex-col">
                {!!item.children?.length && (
                  <>
                    <h5 className="text-primary-black font-medium">
                      {item.title}
                    </h5>
                    <div className="flex items-center justify-start">
                      <div
                        className={cn(
                          "relative grid grid-cols-1 p-5 pl-0",
                          item.children?.length && item.children.length > 24
                            ? "grid-cols-4"
                            : item.children?.length && item.children.length > 16
                              ? "grid-cols-3"
                              : item.children?.length &&
                                  item.children.length > 8
                                ? "grid-cols-2"
                                : "",
                        )}
                      >
                        {item.children?.map((subItem, subIndex) => (
                          <LinkCmp
                            onMouseEnter={() => setHovered(subIndex)}
                            onMouseLeave={() => setHovered(null)}
                            onClick={() => {
                              setHovered(null);
                              setOpenIndex(null);
                            }}
                            className={cn(
                              "py-3 mr-5 leading-none text-[16px] hover:text-primary-black",
                              {
                                "border-b border-secondary-gray1":
                                  subIndex < 21,
                              },
                            )}
                            key={`secondary-${subIndex}`}
                            href={
                              subItem.isBrand
                                ? brandSearchUrl(subItem.title ?? "")
                                : `/categories/${item.id}/${subItem.id}/${subItem.title?.replace(/[^a-zA-Z0-9]/g, "-")}`
                            }
                          >
                            <div className="relative flex items-center gap-2">
                              {subItem.title}
                              <Icon
                                className={cn(
                                  "h-4 w-4 invisible stroke-primary-white rounded-full bg-primary-black p-1",
                                  { visible: hovered === subIndex },
                                )}
                                icon="Arrow"
                              />
                            </div>
                          </LinkCmp>
                        ))}
                      </div>
                    </div>
                  </>
                )}
                {item.isBrand && (
                  <LinkCmp href="/brand" className="self-end">
                    <IconButton variant="underline" icon="ArrowCircle">
                      {t("view_all")}
                    </IconButton>
                  </LinkCmp>
                )}
              </div>
              {item.brands && (
                <div className="flex flex-col">
                  <h5 className="text-primary-black font-medium">
                    {brandsLabel}
                  </h5>
                  <div className="flex items-center justify-start">
                    <div className={cn("relative grid grid-cols-1 p-5 pl-0")}>
                      {item.brands?.map((brand, brandSubIndex) => (
                        <LinkCmp
                          className={cn(
                            "py-3 mr-5 leading-none text-[16px] hover:text-primary-black",
                            {
                              "border-b border-secondary-gray1":
                                brandSubIndex < 7,
                            },
                          )}
                          key={`brands-${brandSubIndex}`}
                          href={brandCategorySearchUrl(brand, item.title)}
                        >
                          <div className="group relative flex items-center gap-2">
                            {brand}
                            <Icon
                              className={cn(
                                "h-4 w-4 invisible stroke-primary-white rounded-full bg-primary-black p-1",
                                "group-hover:visible",
                              )}
                              icon="Arrow"
                            />
                          </div>
                        </LinkCmp>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              {!item.brands && item.imgUrl && (
                <div className="relative overflow-hidden aspect-[13/16] w-[325px] h-full rounded-[20px] ml-8">
                  <img
                    className="w-full h-full object-cover"
                    src={item.imgUrl}
                    alt={item.title}
                  />
                  <LinkCmp
                    href={`/categories/${item.id}/${item.title?.replace(/[^a-zA-Z0-9]/g, "-")}`}
                    className="hover:opacity-95 bg-primary-white px-10 py-3 whitespace-nowrap rounded-[40px] absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
                  >
                    <h5 className="text-primary-black font-medium">
                      {t("view_all")}
                    </h5>
                  </LinkCmp>
                </div>
              )}
            </PopoverContent>
          </Popover>
        ))}
      </nav>

      {/* Mobile */}
      <ToggleGroup
        onValueChange={handleToggle}
        type="single"
        className={cn(
          "hidden max-md:block max-md:mt-[20px] max-md:flex max-md:flex-col divide-y divide-secondary-gray1",
          { "divide-y-0": !!sub },
        )}
      >
        {items.map((item, index) => (
          <div
            key={index}
            className={cn("", { hidden: !!sub && sub !== `${index}` })}
          >
            <ToggleGroupItem
              className="peer py-[16px] menu-toggle"
              value={`${index}`}
            >
              {item.title}
              <Icon className="h-[17px] w-[17px]" icon="Arrow" />
            </ToggleGroupItem>
            <div className="hidden peer-data-[state=on]:block flex flex-col pl-[34px] pr-[16px]">
              {closeAction && (
                <>
                  <LinkCmp
                    onClick={() => closeAction()}
                    key={`secondary-${index}`}
                    href={
                      item.isBrand
                        ? "/brand"
                        : `/categories/${item.id}/${item.title?.replace(/[^a-zA-Z0-9]/g, "-")}`
                    }
                  >
                    <IconButton
                      className="w-full h-auto py-[12px] text-[14px] *:h-[12px] *:w-[12px] border-b rounded-none border-secondary-gray1"
                      variant="ghost"
                      icon="Arrow"
                    >
                      {t("view_all")}
                    </IconButton>
                  </LinkCmp>
                  {item.children?.map((subItem, subIndex) => (
                    <LinkCmp
                      onClick={() => closeAction()}
                      key={`secondary-${subIndex}`}
                      href={
                        subItem.isBrand
                          ? brandSearchUrl(subItem.title ?? "")
                          : `/categories/${item.id}/${subItem.id}/${subItem.title?.replace(/[^a-zA-Z0-9]/g, "-")}`
                      }
                    >
                      <IconButton
                        className="w-full h-auto py-[12px] text-[14px] *:h-[12px] *:w-[12px] border-b rounded-none border-secondary-gray1"
                        variant="ghost"
                        icon="Arrow"
                      >
                        {subItem.title}
                      </IconButton>
                    </LinkCmp>
                  ))}
                  {item.brands && (
                    <>
                      <p className="mt-[40px] mb-[10px]">{brandsLabel}</p>
                      {item.brands.map((brand, subIndex) => (
                        <LinkCmp
                          onClick={() => closeAction()}
                          key={`secondary-${subIndex}`}
                          href={brandCategorySearchUrl(brand, item.title)}
                        >
                          <IconButton
                            className="w-full h-auto py-[12px] text-[14px] *:h-[12px] *:w-[12px] border-b rounded-none border-secondary-gray1"
                            variant="ghost"
                            icon="Arrow"
                          >
                            {brand}
                          </IconButton>
                        </LinkCmp>
                      ))}
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        ))}
      </ToggleGroup>
    </div>
  );
};

export { Secondary };
